import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { ButtonGroup, Button, SegmentLoader, Row, Col, InputControl } from '@si-armes/dsfr-react';
import { getAPI, postAPI, reinitialiseAPI } from 'store/apiReducer';
import usagerClientReducer from 'store/usagerReducer';
import InputFiles from 'components/Commun/Form/InputFiles';
import {
    API_RECUPERER_ADRESSE_LISTE_EXTENSION,
    CONTEXT_API_RECUPERER_ADRESSE_LISTE_EXTENSION,
    API_GET_ENTREPRISE_SIRET,
    CONTEXT_GET_ENTREPRISE_SIRET,
    API_GET_ASSOCIATION_RNA,
    CONTEXT_GET_ASSOCIATION_RNA,
    API_COMPTE_CREER_PERSONNE_MORALE_URL,
    CONTEXT_CREER_COMPTE_ETAPE_PERSONNE_MORALE,
    API_ENREGISTRER_AUTORISATION_EXERCICE_PERSONNE_MORALE_URL,
    CONTEXT_ENREGISTRER_AUTORISATION_EXERCICE_PERSONNE_MORALE,
} from 'constants/apiUrls.constants';
import { REGEXP_EMAIL } from 'constants/contraintes.constants';
import { getMessageErreurChampManquant } from 'utils/utils';
import CreerCompteAdresse from '../adresse/CreerCompteAdresse';
import { statutAfci, statutCnaps } from '../../../../../../constants/metierAssociation.constants';

const InformationsPersonneMorale = ({
    setEtapeInformationPersonneMorale,
    dataInformationsPersonneMorale,
    retourEtape,
    uuidBrouillon,
    dataSiret,
    dataRNA,
    isLoadingSiret,
    isLoadingRNA,
    isLoadingPersonneMorale,
    erreurPersonneMorale,
    statutCompteMetierAsso,
}) => {
    const dispatch = useDispatch();
    const hookForm = useForm({
        mode: 'onChange',
        defaultValues: {
            ...dataInformationsPersonneMorale,
        },
    });

    const {
        register,
        handleSubmit,
        getValues,
        formState: { errors },
        watch,
        setValue,
    } = hookForm;
    const [typeEntreprise, setTypeEntreprise] = useState(null);
    const [isAutofilled, setIsAutofilled] = useState(false);
    const [autofilledFields, setAutofilledFields] = useState({});
    const [formData, setFormData] = useState(null);

    const [isSuccessSubmitCreerCompteAdresse, setIsSuccessSubmitCreerCompteAdresse] =
        useState(false);

    const [isLoadingApiCreerCompteAdresse, setIsLoadingApiCreerCompteAdresse] = useState(false);

    const [addressEtablissement, setAddressEtablissement] = useState();

    const validateSiret = (value) => /^[0-9]{14}$/.test(value);
    const validateRNA = (value) => /^W[0-9]{9}$/i.test(value);

    const detectTypeEntreprise = (value) => {
        if (!value) return null;
        const upperValue = value.toUpperCase();

        if (validateSiret(value)) return 'SIRET';
        if (validateRNA(upperValue)) return 'RNA';
        return null;
    };

    const watchSiren = watch('siren');
    const watchRaisonSociale = watch('raisonSociale');

    const watchEmail = watch('email');
    const watchTelephone = watch('telephone');
    const watchCodePostal = watch('codePostal');
    const watchVille = watch('ville');
    const watchTypeEtNomDeVoie = watch('typeEtNomDeVoie');
    const watchAutorisationExercice = watch('autorisationExercice');

    // Désactiver uniquement les champs qui ont été autofillés par l'API
    // Ne pas désactiver si l'utilisateur tape manuellement
    const disableRaisonSociale = isAutofilled && ['SIRET', 'RNA'].includes(typeEntreprise);
    const disableFormeJuridique = isAutofilled && ['SIRET'].includes(typeEntreprise);
    const disableCapital = isAutofilled && ['SIRET'].includes(typeEntreprise);
    const disableNombreParts = isAutofilled && ['SIRET'].includes(typeEntreprise);

    let labelAutorisation = '';
    let textAideAutorisation = '';
    let afficherAutorisation = false;

    if (statutCnaps.includes(statutCompteMetierAsso?.typeMetierAssociation)) {
        afficherAutorisation = true;
        labelAutorisation = 'CNAPS';
        textAideAutorisation =
            " Entreprises de sécurité privée, convoyeurs, EPPN, établissements de formation : fournir l'autorisation CNAPS. Formats acceptés : PDF, JPG, PNG (max 5Mo).";
    } else if (statutAfci.includes(statutCompteMetierAsso?.typeMetierAssociation)) {
        afficherAutorisation = true;
        labelAutorisation = 'AFCI';
        textAideAutorisation =
            "Établissements de formation aux métiers d'armurerie : fournir l'AFCI. Formats acceptés : PDF, JPG, PNG (max 5Mo).";
    }

    // Vérifier si tous les champs obligatoires sont remplis
    const isFormComplete = () => {
        const hasAutorisationFiles =
            watchAutorisationExercice &&
            Array.isArray(watchAutorisationExercice) &&
            watchAutorisationExercice.length > 0;

        const checkStatutAutorisation =
            statutCnaps.includes(statutCompteMetierAsso?.typeMetierAssociation) ||
            statutAfci.includes(statutCompteMetierAsso?.typeMetierAssociation);

        const defautCheck =
            !!watchSiren &&
            !!watchRaisonSociale &&
            !!watchEmail &&
            !!watchTelephone &&
            !!watchCodePostal &&
            !!watchVille &&
            !!watchTypeEtNomDeVoie;

        if (checkStatutAutorisation) {
            return defautCheck && hasAutorisationFiles;
        }

        return defautCheck;
    };

    // Récupérer la liste des extensions d'adresse au montage du composant
    useEffect(() => {
        dispatch(
            getAPI(
                usagerClientReducer,
                API_RECUPERER_ADRESSE_LISTE_EXTENSION,
                CONTEXT_API_RECUPERER_ADRESSE_LISTE_EXTENSION,
            ),
        );

        return () => {
            dispatch(reinitialiseAPI(API_RECUPERER_ADRESSE_LISTE_EXTENSION));
        };
    }, []);

    const handleChangeSiret = (value) => {
        if (!value) {
            // Si le champ est vide, on purge toutes les champs remplis
            setValue('raisonSociale', '');
            setValue('formeJuridique', '');
            setValue('capital', '');
            setValue('nombreParts', '');
            setValue('numeroDeVoie', '');
            setValue('extension', '');
            setValue('typeEtNomDeVoie', '');
            setValue('lieuDit', '');
            setValue('codePostal', '');
            setValue('ville', '');
            setIsAutofilled(false);
            setAutofilledFields({});

            // On reinitialise tous les états redux liés.
            dispatch(reinitialiseAPI(CONTEXT_GET_ENTREPRISE_SIRET));
            dispatch(reinitialiseAPI(CONTEXT_GET_ASSOCIATION_RNA));
        }
    };

    const callApiSiret = () => {
        const type = detectTypeEntreprise(watchSiren);
        setTypeEntreprise(type);

        // Type non reconnu, ne rien faire
        if (!type) {
            return;
        }

        setTimeout(() => {
            if (type === 'SIRET' && watchSiren.length === 14) {
                dispatch(
                    getAPI(
                        usagerClientReducer,
                        `${API_GET_ENTREPRISE_SIRET}/${watchSiren}`,
                        CONTEXT_GET_ENTREPRISE_SIRET,
                    ),
                );
            } else if (type === 'RNA' && watchSiren.length === 10) {
                dispatch(
                    getAPI(
                        usagerClientReducer,
                        `${API_GET_ASSOCIATION_RNA}/${watchSiren.toUpperCase()}`,
                        CONTEXT_GET_ASSOCIATION_RNA,
                    ),
                );
            }
        }, 500);
    };

    // Auto-fill des champs avec les données API
    useEffect(() => {
        if (dataSiret && typeEntreprise === 'SIRET') {
            const data = dataSiret;
            const filledFields = {};

            // Remplir les champs depuis le DTO SIRET
            if (data?.raisonSociale) {
                setValue('raisonSociale', data?.raisonSociale);
                filledFields.raisonSociale = true;
            }
            if (data?.formeJuridique) {
                setValue('formeJuridique', data?.formeJuridique);
                filledFields.formeJuridique = true;
            }
            if (data?.categorieEntreprise) {
                setValue('capital', data?.categorieEntreprise);
                filledFields.capital = true;
            }

            // Remplir l'adresse si disponible
            if (data?.adresse) {
                if (data?.adresse?.numeroVoie) {
                    setValue('numeroDeVoie', data?.adresse.numeroVoie);
                    filledFields.numeroDeVoie = true;
                }
                if (data?.adresse?.typeVoie && data?.adresse?.libelleVoie) {
                    setValue(
                        'typeEtNomDeVoie',
                        `${data?.adresse?.typeVoie} ${data?.adresse?.libelleVoie}`,
                    );
                    filledFields.typeEtNomDeVoie = true;
                } else if (data?.adresse?.libelleVoie) {
                    setValue('typeEtNomDeVoie', data?.adresse?.libelleVoie);
                    filledFields.typeEtNomDeVoie = true;
                }
                if (data?.adresse?.indiceRepetitionVoie) {
                    setValue('extension', data?.adresse?.indiceRepetitionVoie);
                    filledFields.extension = true;
                }
                if (data?.adresse?.lieuDit) {
                    setValue('lieuDit', data.adresse.lieuDit);
                    filledFields.lieuDit = true;
                }
                if (data?.adresse?.codePostal) {
                    setValue('codePostal', data?.adresse?.codePostal);
                    filledFields.codePostal = true;
                }
                if (data?.adresse?.ville || data?.adresse?.commune) {
                    setValue('ville', data?.adresse?.ville || data?.adresse?.commune);
                    filledFields.ville = true;
                }
            }
            setAutofilledFields(filledFields);

            setIsAutofilled(true);
        } else if (dataRNA && typeEntreprise === 'RNA') {
            const filledFields = {};

            // Remplir les champs depuis le DTO RNA
            if (dataRNA?.titre) {
                setValue('raisonSociale', dataRNA.titre);
                filledFields.raisonSociale = true;
            }

            // Remplir l'adresse si disponible
            if (dataRNA?.adresse) {
                if (dataRNA.adresse.numeroVoie) {
                    setValue('numeroDeVoie', dataRNA?.adresse?.numeroVoie);
                    filledFields.numeroDeVoie = true;
                }
                if (dataRNA?.adresse?.typeVoie && dataRNA?.adresse?.libelleVoie) {
                    setValue(
                        'typeEtNomDeVoie',
                        `${dataRNA?.adresse?.typeVoie} ${dataRNA?.adresse?.libelleVoie}`,
                    );
                    filledFields.typeEtNomDeVoie = true;
                } else if (dataRNA?.adresse?.libelleVoie) {
                    setValue('typeEtNomDeVoie', dataRNA?.adresse?.libelleVoie);
                    filledFields.typeEtNomDeVoie = true;
                }
                if (dataRNA?.adresse?.indiceRepetitionVoie) {
                    setValue('extension', dataRNA?.adresse?.indiceRepetitionVoie);
                    filledFields.extension = true;
                }
                if (dataRNA?.adresse?.lieuDit) {
                    setValue('lieuDit', dataRNA?.adresse?.lieuDit);
                    filledFields.lieuDit = true;
                }
                if (dataRNA?.adresse?.codePostal) {
                    setValue('codePostal', dataRNA?.adresse?.codePostal);
                    filledFields.codePostal = true;
                }
                if (dataRNA?.adresse?.ville || dataRNA?.adresse?.commune) {
                    setValue('ville', dataRNA?.adresse?.ville || dataRNA?.adresse?.commune);
                    filledFields.ville = true;
                }
            }

            setAutofilledFields(filledFields);

            setIsAutofilled(true);
        }
    }, [dataSiret, dataRNA, typeEntreprise, setValue]);

    // Fonction pour uploader les autorisations d'exercice
    const postAutorisationsExercice = async (autorisationFile) => {
        if (!autorisationFile || autorisationFile.length === 0) return;

        dispatch(reinitialiseAPI(CONTEXT_ENREGISTRER_AUTORISATION_EXERCICE_PERSONNE_MORALE));

        const fileToSend = await autorisationFile[0].getFileToSend();

        dispatch(
            postAPI(
                usagerClientReducer,
                API_ENREGISTRER_AUTORISATION_EXERCICE_PERSONNE_MORALE_URL,
                {
                    type: labelAutorisation,
                    autorisationFile: fileToSend,
                    uuidBrouillon,
                },
                CONTEXT_ENREGISTRER_AUTORISATION_EXERCICE_PERSONNE_MORALE,
            ),
        );
    };

    // Gestion de la soumission du formulaire
    const onSubmit = async (data) => {
        // Vérifier si l'adresse a été soumise avec succès
        if (!isSuccessSubmitCreerCompteAdresse) {
            return;
        }

        const formattedData = {
            uuidBrouillon,
            numIdEntreprise: data.siren,
            typeIdentifiant: typeEntreprise,
            raisonSociale: data.raisonSociale,
            formeJuridique: data.formeJuridique || null,
            capital: data.capital || null,
            nombreParts: data.nombreParts || null,
            email: data.email,
            telephone: data.telephone,
            adresse: {
                numVoie: addressEtablissement?.numeroDeVoie || null,
                extension: addressEtablissement?.extension || null,
                libelleVoie: addressEtablissement?.typeEtNomDeVoie,
                lieuDit: addressEtablissement?.lieuDit || null,
                codePostal: addressEtablissement?.codePostal,
                ville: addressEtablissement?.ville,
            },
        };

        setFormData(formattedData);

        // Enregistrement des données vers l'api.
        dispatch(
            postAPI(
                usagerClientReducer,
                API_COMPTE_CREER_PERSONNE_MORALE_URL,
                formattedData,
                CONTEXT_CREER_COMPTE_ETAPE_PERSONNE_MORALE,
            ),
        );

        // Upload du fichier d'autorisation d'exercice
        if (data.autorisationExercice && data.autorisationExercice.length > 0) {
            await postAutorisationsExercice(data.autorisationExercice);
        }
    };

    useEffect(() => {
        if (isSuccessSubmitCreerCompteAdresse && addressEtablissement) {
            onSubmit(getValues());
        }
    }, [isSuccessSubmitCreerCompteAdresse, addressEtablissement]);

    // Passer à l'étape suivante quand l'API a réussi
    useEffect(() => {
        if (
            !isLoadingPersonneMorale &&
            !erreurPersonneMorale &&
            isLoadingPersonneMorale !== null &&
            formData &&
            isSuccessSubmitCreerCompteAdresse
        ) {
            setEtapeInformationPersonneMorale(getValues());
        }
    }, [
        isLoadingPersonneMorale,
        erreurPersonneMorale,
        setEtapeInformationPersonneMorale,
        formData,
    ]);

    return (
        <SegmentLoader isLoading={isLoadingSiret || isLoadingRNA || isLoadingApiCreerCompteAdresse}>
            <FormProvider {...hookForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <h3>Informations</h3>
                    {/* LIGNE 1: SIRET/RNA, Raison sociale, Forme juridique */}
                    <Row gutters>
                        <Col col={12} lg={4}>
                            {/* Label hors du flex */}
                            <label htmlFor="siren" className="fr-label">
                                SIRET OU RNA *
                            </label>

                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                <div style={{ flex: 1 }}>
                                    <InputControl
                                        type="text"
                                        maxLength="14"
                                        id="siren"
                                        uppercase
                                        {...register('siren', {
                                            onChange: ({ target: { value } }) => {
                                                handleChangeSiret(value);
                                            },
                                            required: getMessageErreurChampManquant('SIRET ou RNA'),

                                            validate: (value) => {
                                                if (!value) return true;

                                                const upperValue = value.toUpperCase();

                                                if (value.length === 14) {
                                                    return (
                                                        /^[0-9]{14}$/.test(value) ||
                                                        'SIRET invalide : 14 chiffres requis'
                                                    );
                                                }

                                                if (value.length === 10) {
                                                    return (
                                                        /^W[0-9]{9}$/i.test(upperValue) ||
                                                        'RNA invalide : format W + 9 chiffres requis'
                                                    );
                                                }

                                                return 'Le numéro saisi ne correspond pas au format attendu, veuillez vérifier votre saisie';
                                            },
                                        })}
                                        state={errors.siren?.message ? 'error' : null}
                                        message={errors.siren?.message}
                                        hideLabel
                                    />
                                </div>

                                <Button
                                    type="button"
                                    name="ajouterAdresse"
                                    onClick={callApiSiret}
                                    disabled={isLoadingSiret || isLoadingRNA}
                                >
                                    Rechercher
                                </Button>
                            </div>
                        </Col>

                        <Col col={12} lg={4}>
                            <InputControl
                                label="Raison sociale *"
                                type="text"
                                maxLength="200"
                                id="raisonSociale"
                                uppercase
                                {...register('raisonSociale', {
                                    required: getMessageErreurChampManquant('raison sociale'),
                                    maxLength: {
                                        value: 200,
                                        message:
                                            'La raison sociale ne peut pas dépasser 200 caractères.',
                                    },
                                })}
                                state={errors.raisonSociale?.message ? 'error' : null}
                                message={errors.raisonSociale?.message}
                                disabled={disableRaisonSociale}
                            />
                        </Col>
                        <Col col={12} lg={4}>
                            <InputControl
                                label="Forme juridique"
                                type="text"
                                maxLength="100"
                                id="formeJuridique"
                                uppercase
                                {...register('formeJuridique', {
                                    maxLength: {
                                        value: 100,
                                        message:
                                            'La forme juridique ne peut pas dépasser 100 caractères.',
                                    },
                                })}
                                state={errors.formeJuridique?.message ? 'error' : null}
                                message={errors.formeJuridique?.message}
                                disabled={disableFormeJuridique}
                            />
                        </Col>
                    </Row>
                    {/* LIGNE 2: Capital, Nombre de parts */}
                    <Row gutters>
                        <Col col={12} lg={6}>
                            <InputControl
                                label="Montant du capital de la société (en euros)"
                                type="text"
                                maxLength="12"
                                id="capital"
                                {...register('capital', {
                                    // pattern: {
                                    //     value: /^[0-9]+$/,
                                    //     message: 'Le capital doit être un nombre.',
                                    // },
                                    maxLength: {
                                        value: 12,
                                        message: 'Le capital ne peut pas dépasser 12 chiffres.',
                                    },
                                })}
                                state={errors.capital?.message ? 'error' : null}
                                message={errors.capital?.message}
                                disabled={disableCapital}
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <InputControl
                                label="Nombre de parts"
                                type="text"
                                maxLength="9"
                                id="nombreParts"
                                {...register('nombreParts', {
                                    pattern: {
                                        value: /^[0-9]+$/,
                                        message: 'Le nombre de parts doit être un nombre.',
                                    },
                                    maxLength: {
                                        value: 9,
                                        message:
                                            'Le nombre de parts ne peut pas dépasser 9 chiffres.',
                                    },
                                })}
                                state={errors.nombreParts?.message ? 'error' : null}
                                message={errors.nombreParts?.message}
                                disabled={disableNombreParts}
                            />
                        </Col>
                    </Row>
                    {/* LIGNE 3: Email, Téléphone */}
                    <Row gutters>
                        <Col col={12} lg={6}>
                            <InputControl
                                label="Courriel *"
                                type="email"
                                maxLength="255"
                                id="email"
                                {...register('email', {
                                    required: getMessageErreurChampManquant('courriel'),
                                    pattern: {
                                        value: REGEXP_EMAIL,
                                        message: "Le courriel n'est pas correct.",
                                    },
                                })}
                                state={errors.email?.message ? 'error' : null}
                                message={errors.email?.message}
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <InputControl
                                label="Téléphone *"
                                type="tel"
                                maxLength="14"
                                id="telephone"
                                {...register('telephone', {
                                    required: getMessageErreurChampManquant('téléphone'),
                                    pattern: {
                                        value: /^[0-9]{10}$/,
                                        message:
                                            'Le numéro de téléphone doit contenir 10 chiffres.',
                                    },
                                })}
                                state={errors.telephone?.message ? 'error' : null}
                                message={errors.telephone?.message}
                            />
                        </Col>
                    </Row>

                    <CreerCompteAdresse
                        title="Adresse postale"
                        callApiAdressePrincipale={false}
                        renderFormProvider={false}
                        afficherInformations={false}
                        afficherJustificatifDomicile={false}
                        afficherCertificatMedical={false}
                        autofilledFields={autofilledFields}
                        afficherItenerant={false}
                        desactiverChamps
                        uuidBrouillon={uuidBrouillon}
                        dataAdressePrincipaleRenseignee={dataInformationsPersonneMorale}
                        isAncienLicencie={false}
                        setSuccessSubmit={setIsSuccessSubmitCreerCompteAdresse}
                        onLoadingChange={setIsLoadingApiCreerCompteAdresse}
                        getAddress={setAddressEtablissement}
                    />
                    {/* Autorisation CNAPS ou AFCI selon le type d'établissement */}

                    {afficherAutorisation && (
                        <Row gutters>
                            <Col col={12}>
                                <InputFiles
                                    name="autorisationExercice"
                                    label={`Autorisation d'exercer (${labelAutorisation}) *`}
                                    texteAide={textAideAutorisation}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    tailleMoMaxUnFichier={5}
                                    multiple={false}
                                    required
                                />
                            </Col>
                        </Row>
                    )}

                    <Row className="margin-top-lg">
                        <Col>
                            <ButtonGroup inline align="right">
                                <Button
                                    name="etapePrecedente"
                                    variant="secondary"
                                    type="button"
                                    isLoading={isLoadingRNA || isLoadingApiCreerCompteAdresse}
                                    onClick={retourEtape}
                                >
                                    Retour
                                </Button>
                                <Button
                                    name="etapeSuivante"
                                    variant="primary"
                                    type="submit"
                                    isLoading={isLoadingRNA || isLoadingApiCreerCompteAdresse}
                                    disabled={(() => {
                                        const formComplete = isFormComplete();
                                        const hasErrors = Object.keys(errors).length > 0;

                                        return !formComplete || hasErrors;
                                    })()}
                                >
                                    Accéder à l'étape suivante
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </form>
            </FormProvider>
        </SegmentLoader>
    );
};

InformationsPersonneMorale.propTypes = {
    setEtapeInformationPersonneMorale: PropTypes.func.isRequired,
    retourEtape: PropTypes.func.isRequired,
    uuidBrouillon: PropTypes.string,
    isLoadingListeExtension: PropTypes.bool,
    dataListeExtension: PropTypes.array,
    errorListeExtension: PropTypes.object,
    dataSiret: PropTypes.object,
    dataRNA: PropTypes.object,
    isLoadingSiret: PropTypes.bool,
    isLoadingRNA: PropTypes.bool,
    isLoadingPersonneMorale: PropTypes.bool,
    erreurPersonneMorale: PropTypes.bool,
    getApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    postApi: PropTypes.func.isRequired,
    dataInformationsPersonneMorale: PropTypes.object,
    statutCompteMetierAsso: PropTypes.shape({
        statut: PropTypes.string,
        typeMetierAssociation: PropTypes.string,
    }),
};

export default connect(
    (state) => {
        const {
            isLoading: isLoadingListeExtension,
            data: dataListeExtension,
            erreur: errorListeExtension,
        } = state.adresseExtensions?.recupererListe || {};

        const siretState = state.entreprise?.siret || {};
        const rnaState = state.association?.rna || {};

        const personneMoraleState = state?.compte?.creer?.creerPersonneMorale || {};

        return {
            isLoadingListeExtension,
            dataListeExtension,
            errorListeExtension,

            dataSiret: siretState.data,
            isLoadingSiret: siretState.isLoading,
            dataRNA: rnaState.data,
            isLoadingRNA: rnaState.isLoading,
            isLoadingPersonneMorale: personneMoraleState.isLoading,
            erreurPersonneMorale: personneMoraleState.erreur,
            statutCompteMetierAsso: state.compte?.creer?.statutCompteMetierAsso,
        };
    },
    {
        getApi: getAPI,
        reinitialiseApi: reinitialiseAPI,
        postApi: postAPI,
    },
)(InformationsPersonneMorale);
